// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Archive, Construction, Copy, MoreHorizontal, Power, PowerOff, ShelvingUnit, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SettingsCompareIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";
import { cn } from "@/lib/utils";

interface StoreActionsMenuProps {
  isActive: boolean;
  onDuplicate: () => void;
  onCompareSettings: () => void;
  onToggleActive: () => void;
  triggerClassName?: string;
}

export function StoreActionsMenu({
  isActive,
  onDuplicate,
  onCompareSettings,
  onToggleActive,
  triggerClassName,
}: StoreActionsMenuProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesEdit);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("h-8 w-8", triggerClassName)}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
        {canEdit && (
          <DropdownMenuItem onSelect={onDuplicate}>
            <Copy className="h-4 w-4" />
            {t("stores.list.actions.duplicate")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onCompareSettings}>
          <SettingsCompareIcon className="h-4 w-4" />
          {t("stores.list.actions.compareSettings")}
        </DropdownMenuItem>
        {canEdit && (
          <>
            {/* Tools submenu is all WIP (mocked) — debug-mode only for now. */}
            {debugMode && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Wrench className="h-4 w-4" />
                    {t("stores.list.actions.tools")}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onSelect={() => toast.warning(t("common.workInProgress"))}
                      className="text-amber-600 underline focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
                    >
                      <ShelvingUnit className="h-4 w-4" />
                      <Construction className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                      {t("stores.list.actions.triggerShc")}
                    </DropdownMenuItem>
                    {!isActive && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => toast.warning(t("common.workInProgress"))}
                          className="text-amber-600 underline focus:text-amber-600 dark:text-amber-400 dark:focus:text-amber-400"
                        >
                          <Archive className="h-4 w-4 text-destructive" />
                          <Construction className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                          {t("stores.list.actions.archive")}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onToggleActive}
              className={isActive ? "text-destructive focus:text-destructive" : ""}
            >
              {isActive ? (
                <>
                  <PowerOff className="h-4 w-4 text-destructive" />
                  {t("stores.list.actions.deactivate")}
                </>
              ) : (
                <>
                  <Power className="h-4 w-4" />
                  {t("stores.list.actions.activate")}
                </>
              )}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
